/**
 * BeezFy Wishlist Premium System
 * Sistema wishlist ultra-premium con integrazione drawer system
 * Performance ottimizzate e UX di alto livello
 * Version: 1.0.2 - Fixed variant ID and cache issues
 * Updated: 2024-01-25T14:30:00
 */

console.log('[Wishlist] Script loaded at:', new Date().toISOString());

class BeezfyWishlistPremium {
  constructor() {
    this.storageKey = 'beezfy-wishlist';
    this.wishlistData = [];
    this.productCache = new Map();
    this.isInitialized = false;
    this.drawer = null;
    this.currentLanguage = document.documentElement.lang || 'it';

    this.selectors = {
      drawer: '[data-beezfy-drawer="wishlist"]',
      headerIcon: '.action-wishlist',
      headerCount: '.beezfy-wishlist-header-count',
      drawerCount: '[data-wishlist-count]',
      content: '[data-wishlist-content]',
      empty: '[data-wishlist-empty]',
      loading: '[data-wishlist-loading]',
      items: '[data-wishlist-items]',
      footer: '[data-wishlist-footer]',
      clearBtn: '[data-wishlist-clear]',
      shareBtn: '[data-wishlist-share]',
      wishlistBtn: '.beezfy-wishlist-btn-premium, .wishlist-button, .card__icon--wishlist'
    };

    this.translations = {
      it: {
        added: 'Aggiunto alla lista desideri',
        removed: 'Rimosso dalla lista desideri',
        cleared: 'Lista desideri svuotata',
        error: 'Si è verificato un errore',
        addedToCart: 'Aggiunto al carrello',
        allAddedToCart: 'Tutti i prodotti aggiunti al carrello',
        confirmClear: 'Vuoi davvero svuotare la lista desideri?',
        loading: 'Caricamento...',
        empty: 'La tua lista desideri è vuota',
        emptyMessage: 'Salva qui i tuoi prodotti preferiti',
        continueShopping: 'Continua lo shopping',
        addToCart: 'Aggiungi al carrello',
        remove: 'Rimuovi',
        unavailable: 'Non disponibile',
        share: 'Condividi',
        clearAll: 'Svuota tutto'
      },
      en: {
        added: 'Added to wishlist',
        removed: 'Removed from wishlist',
        cleared: 'Wishlist cleared',
        error: 'An error occurred',
        addedToCart: 'Added to cart',
        allAddedToCart: 'All products added to cart',
        confirmClear: 'Are you sure you want to clear the wishlist?',
        loading: 'Loading...',
        empty: 'Your wishlist is empty',
        emptyMessage: 'Save your favorite products here',
        continueShopping: 'Continue shopping',
        addToCart: 'Add to cart',
        remove: 'Remove',
        unavailable: 'Unavailable',
        share: 'Share',
        clearAll: 'Clear all'
      },
      es: {
        added: 'Añadido a la lista de deseos',
        removed: 'Eliminado de la lista de deseos',
        cleared: 'Lista de deseos vaciada',
        error: 'Se produjo un error',
        addedToCart: 'Añadido al carrito',
        allAddedToCart: 'Todos los productos añadidos al carrito',
        confirmClear: '¿Estás seguro de que quieres vaciar la lista de deseos?',
        loading: 'Cargando...',
        empty: 'Tu lista de deseos está vacía',
        emptyMessage: 'Guarda aquí tus productos favoritos',
        continueShopping: 'Continuar comprando',
        addToCart: 'Añadir al carrito',
        remove: 'Eliminar',
        unavailable: 'No disponible',
        share: 'Compartir',
        clearAll: 'Vaciar todo'
      },
      fr: {
        added: 'Ajouté à la liste de souhaits',
        removed: 'Retiré de la liste de souhaits',
        cleared: 'Liste de souhaits vidée',
        error: 'Une erreur s\'est produite',
        addedToCart: 'Ajouté au panier',
        allAddedToCart: 'Tous les produits ajoutés au panier',
        confirmClear: 'Êtes-vous sûr de vouloir vider la liste de souhaits?',
        loading: 'Chargement...',
        empty: 'Votre liste de souhaits est vide',
        emptyMessage: 'Enregistrez vos produits préférés ici',
        continueShopping: 'Continuer vos achats',
        addToCart: 'Ajouter au panier',
        remove: 'Retirer',
        unavailable: 'Non disponible',
        share: 'Partager',
        clearAll: 'Tout vider'
      },
      de: {
        added: 'Zur Wunschliste hinzugefügt',
        removed: 'Von der Wunschliste entfernt',
        cleared: 'Wunschliste geleert',
        error: 'Ein Fehler ist aufgetreten',
        addedToCart: 'In den Warenkorb gelegt',
        allAddedToCart: 'Alle Produkte in den Warenkorb gelegt',
        confirmClear: 'Möchten Sie wirklich die Wunschliste leeren?',
        loading: 'Laden...',
        empty: 'Ihre Wunschliste ist leer',
        emptyMessage: 'Speichern Sie hier Ihre Lieblingsprodukte',
        continueShopping: 'Weiter einkaufen',
        addToCart: 'In den Warenkorb',
        remove: 'Entfernen',
        unavailable: 'Nicht verfügbar',
        share: 'Teilen',
        clearAll: 'Alles löschen'
      }
    };

    this.init();
  }

  init() {
    if (this.isInitialized) return;

    console.log('[Wishlist] INIT - Version: 2.2 - Time:', new Date().toISOString());

    this.loadWishlist();
    this.setupEventListeners();
    this.updateAllCounts();
    this.syncButtons();
    this.isInitialized = true;

    // Integrazione con drawer system
    if (window.BeezfyDrawerSystem) {
      this.integrateWithDrawerSystem();
    }
  }

  integrateWithDrawerSystem() {
    // Ascolta l'apertura del drawer wishlist
    document.addEventListener('drawer:opened', (e) => {
      if (e.detail && e.detail.type === 'wishlist') {
        this.renderWishlistDrawer();
      }
    });

    // Registra il drawer se non esiste
    const drawer = document.querySelector(this.selectors.drawer);
    if (drawer && !drawer.dataset.beezfyDrawer) {
      drawer.dataset.beezfyDrawer = 'wishlist';
      drawer.dataset.position = 'right';
    }
  }

  setupEventListeners() {
    // Track products being added to cart from wishlist
    this.productsBeingAddedToCart = new Set();

    // Gestione click sui pulsanti wishlist
    document.addEventListener('click', (e) => {
      const btn = e.target.closest(this.selectors.wishlistBtn);
      if (btn) {
        e.preventDefault();
        e.stopPropagation();
        this.toggleProduct(btn);
      }

      // Gestione rimozione dal drawer
      const removeBtn = e.target.closest('[data-wishlist-remove]');
      if (removeBtn) {
        console.log('[Wishlist] Remove button clicked!', removeBtn);
        e.preventDefault();
        e.stopPropagation();
        const productId = removeBtn.dataset.wishlistRemove;
        console.log('[Wishlist] Removing product ID:', productId);
        this.removeProduct(productId, e.target.closest('.beezfy-wishlist-premium__item'));
      }

      // Gestione add to cart dal drawer
      if (e.target.closest('[data-wishlist-add-cart]')) {
        e.preventDefault();
        const btn = e.target.closest('[data-wishlist-add-cart]');
        const productId = btn.dataset.wishlistAddCart;
        const originalId = btn.dataset.wishlistOriginalId || productId;
        console.log('[Wishlist] Add to cart clicked - productId:', productId, 'originalId:', originalId);
        // Mark this product as being added to cart
        this.productsBeingAddedToCart.add(originalId);
        // Pass both IDs - productId for cart, originalId for removal
        this.addToCart(productId, e, originalId);
      }

      // Clear all
      if (e.target.closest(this.selectors.clearBtn)) {
        e.preventDefault();
        this.clearWishlist();
      }

      // Removed add all to cart - violates ATC centralized system rules

      // Share
      if (e.target.closest(this.selectors.shareBtn)) {
        e.preventDefault();
        this.shareWishlist();
      }
    });

    // Listen for successful cart additions
    window.addEventListener('cart:updated', () => {
      // Check if we have products pending removal
      if (this.productsBeingAddedToCart && this.productsBeingAddedToCart.size > 0) {
        console.log('[Wishlist] Cart updated, removing products from wishlist:', Array.from(this.productsBeingAddedToCart));

        // Create a copy of the set before clearing
        const productsToRemove = new Set(this.productsBeingAddedToCart);

        // Clear the set first to avoid duplicate removals
        this.productsBeingAddedToCart.clear();

        // Remove all products that were being added to cart
        productsToRemove.forEach(productId => {
          this.removeFromWishlistAfterCart(productId);
        });
      }
    });

    // Sincronizzazione tra tab
    // IMPORTANTE: l'evento storage si attiva SOLO per cambiamenti da altre tab/finestre
    // Storage event ONLY fires for changes in OTHER tabs/windows
    // It does NOT fire in the same tab - this is by design
    // We handle same-tab updates directly in our methods
    this.storageHandler = (e) => {
      if (e.key === this.storageKey) {
        // Check if this is genuinely from another tab
        // Some browsers have bugs where they fire in the same tab
        const newData = e.newValue ? JSON.parse(e.newValue) : [];
        const currentDataString = JSON.stringify(this.wishlistData);
        const newDataString = JSON.stringify(newData);

        if (currentDataString === newDataString) {
          console.log('[Wishlist] Ignoring storage event - data is identical');
          return;
        }

        console.log('[Wishlist] Processing storage event from another tab');
        // Reload data from storage when changed in another tab
        this.loadWishlist();
        this.updateAllCounts();
        this.syncButtons();
        // Update drawer if it's open
        if (this.isDrawerOpen()) {
          this.renderWishlistDrawer();
        }
      }
    };
    window.addEventListener('storage', this.storageHandler);

    // Gestione header icon click per aprire drawer
    const headerIcon = document.querySelector(this.selectors.headerIcon);
    if (headerIcon) {
      headerIcon.addEventListener('click', (e) => {
        e.preventDefault();
        this.openDrawer();
      });
    }
  }

  loadWishlist() {
    console.log('[Wishlist] loadWishlist called - Stack trace:', new Error().stack);
    try {
      const stored = localStorage.getItem(this.storageKey);
      const previousData = [...this.wishlistData]; // Keep track of previous data
      this.wishlistData = stored ? JSON.parse(stored) : [];

      // Log if data changed
      if (JSON.stringify(previousData) !== JSON.stringify(this.wishlistData)) {
        console.log('[Wishlist] Data changed in loadWishlist:', {
          before: previousData,
          after: this.wishlistData
        });
      }

      // Check for old wishlist format (from previous wishlist.js)
      const oldWishlist = localStorage.getItem('wishlist');
      if (oldWishlist && !stored) {
        try {
          const oldData = JSON.parse(oldWishlist);
          if (Array.isArray(oldData)) {
            // Clear old numeric IDs as we can't fetch product data without handles
            this.wishlistData = [];
            this.saveWishlist();  // Save empty wishlist
            localStorage.removeItem('wishlist');  // Remove old format
          }
        } catch(e) {
          console.error('Error migrating old wishlist:', e);
        }
      }

      // Clean up invalid entries (numeric IDs without product data)
      this.wishlistData = this.wishlistData.filter(item => {
        if (typeof item === 'string' && /^\d+$/.test(item)) {
          // Remove numeric string IDs that can't be fetched
          return false;
        }
        if (item && typeof item === 'object' && item.handle) {
          // Keep objects with handle
          return true;
        }
        // Remove anything else
        return false;
      });

      // Save cleaned data if we removed items
      if (stored && JSON.stringify(this.wishlistData) !== stored) {
        this.saveWishlist();
      }
    } catch (error) {
      console.error('Error loading wishlist:', error);
      this.wishlistData = [];
    }
  }

  saveWishlist() {
    try {
      console.log('[Wishlist] Saving wishlist, size:', this.wishlistData.length, 'items:', this.wishlistData.map(item => typeof item === 'object' ? item.id : item));

      localStorage.setItem(this.storageKey, JSON.stringify(this.wishlistData));
      this.updateAllCounts();

      // Dispatch custom event for same-tab listeners
      window.dispatchEvent(new CustomEvent('wishlist:updated', {
        detail: { wishlist: this.wishlistData }
      }));
    } catch (error) {
      console.error('Error saving wishlist:', error);
    }
  }

  toggleProduct(button) {
    const productId = this.getProductId(button);
    if (!productId) return;

    const isInWishlist = this.isProductInWishlist(productId);

    if (isInWishlist) {
      this.removeProduct(productId);
      this.updateButtonState(button, false);
      this.showNotification(this.t('removed'), 'info');
    } else {
      this.addProduct(button);
      this.updateButtonState(button, true);
      this.showNotification(this.t('added'), 'success');
    }
  }

  addProduct(button) {
    const productData = this.extractProductData(button);
    if (!productData) return;


    // Verifica duplicati
    if (!this.isProductInWishlist(productData.id)) {
      this.wishlistData.push(productData);
      this.saveWishlist();
      console.log('[Wishlist] After adding, wishlist contains:', JSON.stringify(this.wishlistData));
    } else {
      console.log('[Wishlist] Product already in wishlist');
    }
  }

  removeProduct(productId, elementToAnimate = null) {
    console.log('[Wishlist] removeProduct called with:', productId, 'type:', typeof productId);
    console.log('[Wishlist] Current wishlist data:', JSON.stringify(this.wishlistData));

    const performRemoval = () => {
      const targetId = String(productId);
      const beforeCount = this.wishlistData.length;

      this.wishlistData = this.wishlistData.filter(item => {
        if (typeof item === 'string') {
          const keep = String(item) !== targetId;
          if (!keep) {
            console.log('[Wishlist] Removing string item:', item);
          }
          return keep;
        } else {
          // For objects, check all possible ID fields
          const shouldRemove = String(item.id) === targetId ||
                              (item.productId && String(item.productId) === targetId) ||
                              (item.handle && String(item.handle) === targetId);

          if (shouldRemove) {
            console.log('[Wishlist] Removing object item:', {
              id: item.id,
              productId: item.productId,
              handle: item.handle,
              targetId: targetId
            });
          }
          return !shouldRemove;
        }
      });

      console.log(`[Wishlist] After removal: ${beforeCount} -> ${this.wishlistData.length} items`);
      console.log('[Wishlist] Remaining data:', JSON.stringify(this.wishlistData));

      this.saveWishlist();
      this.syncButtons();

      // Always update drawer if it's open
      if (this.isDrawerOpen()) {
        this.renderWishlistDrawer();
      }
    };

    if (elementToAnimate) {
      elementToAnimate.classList.add('removing');
      setTimeout(performRemoval, 400);
    } else {
      performRemoval();
    }
  }

  extractProductData(button) {
    const productCard = button.closest('[data-product-card], .product-card, .card-wrapper, .grid__item');
    if (!productCard) return null;

    // Get both product ID and variant ID
    const variantId = this.getProductId(button); // This method prioritizes variant ID

    // Also try to get the actual product ID for reference
    const productIdEl = button.dataset.productId ||
                       button.closest('[data-product-id]')?.dataset.productId ||
                       productCard.dataset.productId;

    const titleEl = productCard.querySelector('.card__heading a, .product-title, .h4 a, [data-product-title]');
    const imageEl = productCard.querySelector('img');
    const priceEl = productCard.querySelector('.price__regular .price-item, .price, [data-price]');
    const comparePriceEl = productCard.querySelector('.price__sale .price-item--regular, .compare-price, [data-compare-price]');
    const vendorEl = productCard.querySelector('.card__vendor, .vendor, [data-vendor]');
    const linkEl = productCard.querySelector('a[href*="/products/"]');

    // Check if product is available (look for sold out indicators)
    const soldOutBadge = productCard.querySelector('.badge--sold-out, .sold-out, [data-sold-out]');
    const isAvailable = !soldOutBadge;

    // Extract handle from URL
    const handle = linkEl?.href?.split('/products/')[1]?.split('?')[0] || null;

    return {
      id: variantId, // Store the variant ID as primary ID for ATC
      productId: productIdEl, // Store product ID separately if available
      title: titleEl?.textContent?.trim() || 'Product',
      handle: handle, // Store handle for fetching product data later
      image: imageEl?.src || imageEl?.dataset.src,
      price: priceEl?.textContent?.trim(),
      comparePrice: comparePriceEl?.textContent?.trim(),
      vendor: vendorEl?.textContent?.trim(),
      url: linkEl?.href || `/products/${handle || variantId}`,
      available: true, // Default to true, will be updated when fetching
      addedAt: Date.now()
    };
  }

  getProductId(button) {
    // First, look for variant ID in the button itself or parent elements
    const directVariantId = button.dataset.variantId ||
           button.closest('[data-variant-id]')?.dataset.variantId ||
           button.closest('[data-product-variant-id]')?.dataset.productVariantId;

    if (directVariantId) return directVariantId;

    // Look for variant ID in form inputs within the product card
    const productCard = button.closest('[data-product-card], .product-card, .card-wrapper, .grid__item');
    if (productCard) {
      // Check for variant select or input
      const variantInput = productCard.querySelector('[name="id"]') ||
                          productCard.querySelector('select[name="id"]') ||
                          productCard.querySelector('input[name="id"][type="hidden"]');

      if (variantInput?.value) {
        return variantInput.value;
      }

      // Check for first available variant in data attributes
      const firstVariant = productCard.querySelector('[data-variant-id]');
      if (firstVariant?.dataset.variantId) {
        return firstVariant.dataset.variantId;
      }
    }

    // Look in parent forms
    const form = button.closest('form[data-product-form], form.product-form');
    if (form) {
      const formVariantInput = form.querySelector('[name="id"]');
      if (formVariantInput?.value) return formVariantInput.value;
    }

    // Otherwise fallback to product ID (will need to fetch variant later)
    return button.dataset.productId ||
           button.closest('[data-product-id]')?.dataset.productId ||
           productCard?.dataset.productId;
  }

  isProductInWishlist(productId) {
    if (!productId) return false;

    const targetId = String(productId);
    return this.wishlistData.some(item => {
      if (typeof item === 'string') {
        return String(item) === targetId;
      }
      // Check all possible ID fields for objects
      return String(item.id) === targetId ||
             (item.productId && String(item.productId) === targetId) ||
             (item.handle && String(item.handle) === targetId);
    });
  }

  updateButtonState(button, isActive) {
    if (!button) return;

    if (isActive) {
      button.classList.add('is-active');
      button.setAttribute('aria-pressed', 'true');

      const svg = button.querySelector('svg');
      if (svg) {
        svg.style.fill = 'var(--beezfy-wishlist-accent, #ff0066)';
        svg.style.stroke = 'var(--beezfy-wishlist-accent, #ff0066)';
      }
    } else {
      button.classList.remove('is-active');
      button.setAttribute('aria-pressed', 'false');

      const svg = button.querySelector('svg');
      if (svg) {
        svg.style.fill = 'none';
        svg.style.stroke = 'currentColor';
      }
    }
  }

  syncButtons() {
    document.querySelectorAll(this.selectors.wishlistBtn).forEach(button => {
      const productId = this.getProductId(button);
      if (productId) {
        this.updateButtonState(button, this.isProductInWishlist(productId));
      }
    });
  }

  updateAllCounts() {
    const count = this.wishlistData.length;

    // Update header count
    document.querySelectorAll(this.selectors.headerCount).forEach(el => {
      el.textContent = count;
      el.dataset.count = count;
      el.style.display = count > 0 ? 'flex' : 'none';

      // Add/remove double-digit class for smaller font (prevent badge distortion)
      if (count >= 10) {
        el.classList.add('badge-double-digit');
      } else {
        el.classList.remove('badge-double-digit');
      }
    });

    // Update drawer count
    document.querySelectorAll(this.selectors.drawerCount).forEach(el => {
      el.textContent = count;
      el.style.display = count > 0 ? 'inline-flex' : 'none';

      // Add/remove double-digit class for drawer count too
      if (count >= 10) {
        el.classList.add('badge-double-digit');
      } else {
        el.classList.remove('badge-double-digit');
      }
    });

    // Add count to header icon if not exists
    const headerIcon = document.querySelector(this.selectors.headerIcon);
    if (headerIcon && !headerIcon.querySelector(this.selectors.headerCount)) {
      const countBadge = document.createElement('span');
      countBadge.className = 'beezfy-wishlist-header-count';
      countBadge.dataset.count = count;
      countBadge.textContent = count;
      countBadge.style.display = count > 0 ? 'flex' : 'none';

      // Add double-digit class if needed
      if (count >= 10) {
        countBadge.classList.add('badge-double-digit');
      }

      headerIcon.appendChild(countBadge);
    }
  }

  async renderWishlistDrawer() {
    const container = document.querySelector(this.selectors.items);
    const emptyState = document.querySelector(this.selectors.empty);
    const loadingState = document.querySelector(this.selectors.loading);
    const footer = document.querySelector(this.selectors.footer);

    if (!container) return;

    // Don't reload wishlist data - use current in-memory data
    // this.loadWishlist(); // REMOVED - this was causing products to reappear

    console.log('Wishlist data:', this.wishlistData);  // Debug

    // Show loading
    if (loadingState) {
      loadingState.style.display = 'flex';
      if (emptyState) emptyState.style.display = 'none';
      container.style.display = 'none';
      if (footer) footer.style.display = 'none';
    }

    // Simula caricamento per UX
    await new Promise(resolve => setTimeout(resolve, 300));

    if (this.wishlistData.length === 0) {
      // Show empty state
      if (loadingState) loadingState.style.display = 'none';
      if (emptyState) emptyState.style.display = 'flex';
      container.style.display = 'none';
      if (footer) footer.style.display = 'none';
      return;
    }

    // Render items
    const itemsHTML = await this.generateItemsHTML();
    container.innerHTML = itemsHTML;


    // Show content
    if (loadingState) loadingState.style.display = 'none';
    if (emptyState) emptyState.style.display = 'none';
    container.style.display = 'block';
    if (footer) footer.style.display = 'flex';
  }

  async generateItemsHTML() {
    const items = await Promise.all(this.wishlistData.map(async (item) => {
      // Store the original ID from wishlistData
      const originalId = typeof item === 'string' ? item : item.id;

      // Se è solo un ID, prova a caricare i dati del prodotto
      if (typeof item === 'string') {
        item = await this.fetchProductData(item);
      } else if (item.handle) {
        // Pass the handle directly if we have it, otherwise pass the whole item
        const freshData = await this.fetchProductDataByHandle(item.handle, item.id);
        // Merge fresh data with existing item, prioritizing fresh availability
        item = { ...item, ...freshData };
      } else {
        // If no handle, we can't fetch fresh data - use what we have
      }

      // IMPORTANT: Preserve the original ID for removal
      // This ensures the remove button uses the same ID that was stored in wishlistData
      if (!item.originalId) {
        item.originalId = originalId;
      }

      console.log('[Wishlist] Rendering item:', {
        id: item.id,
        title: item.title,
        available: item.available,
        handle: item.handle
      });

      const discount = this.calculateDiscount(item.price, item.comparePrice);

      // Use originalId for the remove button to match what's stored in wishlistData
      const removeId = item.originalId || item.id || item.productId || item.handle || 'unknown';


      return `
        <div class="beezfy-wishlist-premium__item" data-product-id="${removeId}">
          <a href="${item.url}" class="beezfy-wishlist-premium__image">
            ${discount > 0 ? `<span class="beezfy-wishlist-premium__badge">-${discount}%</span>` : ''}
            <img src="${item.image || ''}" alt="${item.title}" loading="lazy">
          </a>
          <div class="beezfy-wishlist-premium__details">
            ${item.vendor ? `<div class="beezfy-wishlist-premium__vendor">${item.vendor}</div>` : ''}
            <a href="${item.url}" class="beezfy-wishlist-premium__title">${item.title}</a>
            <div class="beezfy-wishlist-premium__price">
              <span class="beezfy-wishlist-premium__price-current">${item.price}</span>
              ${item.comparePrice ? `<span class="beezfy-wishlist-premium__price-compare">${item.comparePrice}</span>` : ''}
            </div>
            <div class="beezfy-wishlist-premium__actions">
              ${item.available ?
                `<button class="beezfy-wishlist-premium__btn beezfy-wishlist-premium__btn--primary"
                         data-wishlist-add-cart="${item.id}"
                         data-wishlist-original-id="${removeId}">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <circle cx="9" cy="21" r="1"></circle>
                    <circle cx="20" cy="21" r="1"></circle>
                    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
                  </svg>
                  ${this.t('addToCart')}
                </button>` :
                `<button class="beezfy-wishlist-premium__btn beezfy-wishlist-premium__btn--disabled" disabled>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="8" y1="12" x2="16" y2="12"></line>
                  </svg>
                  ${this.t('unavailable')}
                </button>`
              }
            </div>
          </div>
          <button class="beezfy-wishlist-premium__btn--remove" data-wishlist-remove="${removeId}" aria-label="${this.t('remove')}">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
      `;
    }));

    return `<div class="beezfy-wishlist-premium__grid">${items.join('')}</div>`;
  }

  async fetchProductDataByHandle(handle, variantId = null) {
    // Check cache by handle
    const cacheKey = `${handle}_${variantId || 'default'}`;
    if (this.productCache.has(cacheKey)) {
      return this.productCache.get(cacheKey);
    }

    try {
      const endpoint = `/products/${handle}.js`;
      console.log('[Wishlist] Fetching by handle:', endpoint);

      const response = await fetch(endpoint);
      if (!response.ok) {
        throw new Error(`Failed to fetch: ${response.status}`);
      }

      const data = await response.json();

      // Find the matching variant or get first available
      let selectedVariant = null;
      let finalVariantId = variantId;
      let isAvailable = false;

      if (data.variants && data.variants.length > 0) {
        // If we have a variant ID, find it
        if (variantId) {
          selectedVariant = data.variants.find(v => v.id == variantId);
        }

        // If no match, get first available variant
        if (!selectedVariant) {
          selectedVariant = data.variants.find(v => v.available) || data.variants[0];
        }

        if (selectedVariant) {
          finalVariantId = selectedVariant.id;
          isAvailable = selectedVariant.available;
        }
      }

      // Format image URL properly
      let imageUrl = selectedVariant?.featured_image || data.featured_image || data.images?.[0] || '';

      // Ensure imageUrl is a string
      if (typeof imageUrl !== 'string') {
        imageUrl = imageUrl?.src || imageUrl?.url || String(imageUrl || '');
      }

      // Add protocol/path if needed
      if (imageUrl && !imageUrl.startsWith('http') && !imageUrl.startsWith('//')) {
        imageUrl = imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`;
      }

      const productData = {
        id: finalVariantId,
        productId: data.id,
        title: data.title,
        handle: data.handle,
        image: imageUrl,
        price: selectedVariant ? this.formatMoney(selectedVariant.price) : this.formatMoney(data.price),
        comparePrice: selectedVariant?.compare_at_price ? this.formatMoney(selectedVariant.compare_at_price) : null,
        vendor: data.vendor,
        url: `/products/${data.handle}`,
        available: isAvailable,
        variantTitle: selectedVariant?.title !== 'Default Title' ? selectedVariant?.title : null,
        hasMultipleVariants: data.variants && data.variants.length > 1,
        variants: data.variants // Store variants for variant wizard check
      };

      this.productCache.set(cacheKey, productData);
      return productData;
    } catch (error) {
      console.error('[Wishlist] Error fetching by handle:', handle, error);
      // Return basic data if fetch fails
      return {
        id: variantId || handle,
        title: 'Product',
        handle: handle,
        image: '',
        price: '€0,00',
        url: `/products/${handle}`,
        available: false
      };
    }
  }

  async fetchProductData(productId) {
    // Check cache
    if (this.productCache.has(productId)) {
      return this.productCache.get(productId);
    }

    try {
      // Check if it's a variant ID (Shopify variant IDs are very large numbers)
      const isVariantId = /^\d{10,}$/.test(productId.toString());
      let productHandle = productId;
      let targetVariantId = productId;

      // If we only have numeric ID, we can't directly fetch product data
      // We need to store handle when saving to wishlist
      // For now, return minimal data that will work with existing wishlist items
      if (/^\d+$/.test(productId.toString())) {
        // This is a numeric ID, we can't fetch product directly
        // Check if we have any stored data
        const storedItem = this.wishlistData.find(item =>
          (typeof item === 'object' && item.id == productId) ||
          (typeof item === 'string' && item == productId)
        );

        if (storedItem && typeof storedItem === 'object' && storedItem.handle) {
          productHandle = storedItem.handle;
        } else {
          // Can't fetch without handle, return basic data
          return {
            id: productId,
            title: 'Product',
            handle: productId,
            image: '/cdn/shop/files/placeholder.jpg',
            price: '€0,00',
            url: '#',
            available: true
          };
        }
      }

      // Try to fetch product data by handle
      const endpoint = `/products/${productHandle}.js`;
      console.log('[Wishlist] Fetching product from:', endpoint);

      const response = await fetch(endpoint).catch(() => null);

      if (response?.ok) {
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          // Not JSON, probably got HTML instead
          console.error('[Wishlist] Invalid response format for:', endpoint, 'Content-Type:', contentType);
          throw new Error('Invalid response format');
        }

        const data = await response.json();

        console.log('[Wishlist] Product data fetched:', {
          handle: productHandle,
          productId: data.id,
          variantsCount: data.variants?.length,
          availableVariants: data.variants?.filter(v => v.available).length,
          targetVariantId: targetVariantId
        });

        // Find the matching variant or get first available
        let selectedVariant = null;
        let variantId = targetVariantId;
        let isAvailable = false;

        if (data.variants && data.variants.length > 0) {
          // If we have a variant ID, find it
          if (isVariantId) {
            selectedVariant = data.variants.find(v => v.id == targetVariantId);
            console.log('[Wishlist] Looking for variant:', targetVariantId, 'Found:', !!selectedVariant);
          }

          // If no match or not a variant ID, get first available variant
          if (!selectedVariant) {
            selectedVariant = data.variants.find(v => v.available) || data.variants[0];
            console.log('[Wishlist] Selected fallback variant:', selectedVariant?.id, 'Available:', selectedVariant?.available);
          }

          if (selectedVariant) {
            variantId = selectedVariant.id;
            isAvailable = selectedVariant.available;
          } else {
            // If no variant found, check if product has any available variant
            isAvailable = data.variants.some(v => v.available);
            // Use first variant ID as fallback
            variantId = data.variants[0]?.id || targetVariantId;
          }
        } else {
          // Product has no variants, check general availability
          isAvailable = data.available !== false;
        }

        console.log('[Wishlist] Final availability:', {
          variantId: variantId,
          isAvailable: isAvailable,
          selectedVariant: selectedVariant?.id
        });

        // Format image URL properly
        let imageUrl = selectedVariant?.featured_image || data.featured_image || data.images?.[0] || '';
        if (typeof imageUrl !== 'string') {
          imageUrl = imageUrl?.src || imageUrl?.url || String(imageUrl || '');
        }
        if (imageUrl && !imageUrl.startsWith('http') && !imageUrl.startsWith('//')) {
          imageUrl = imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`;
        }

        const productData = {
          id: variantId,  // Store variant ID for ATC
          productId: data.id,
          title: data.title,
          handle: data.handle,
          image: imageUrl,
          price: selectedVariant ? this.formatMoney(selectedVariant.price) : this.formatMoney(data.price),
          comparePrice: selectedVariant?.compare_at_price ? this.formatMoney(selectedVariant.compare_at_price) :
                       (data.compare_at_price ? this.formatMoney(data.compare_at_price) : null),
          vendor: data.vendor,
          url: `/products/${data.handle}`,
          available: isAvailable,
          variantTitle: selectedVariant?.title !== 'Default Title' ? selectedVariant?.title : null
        };

        this.productCache.set(productId, productData);
        return productData;
      }
    } catch (error) {
      console.error('Error fetching product:', error);
    }

    // Return basic data if fetch fails
    return {
      id: productId,
      title: 'Product',
      handle: productId,
      image: '/cdn/shop/files/placeholder.jpg',
      price: '€0,00',
      url: '#',
      available: true
    };
  }

  formatMoney(cents) {
    const currency = window.Shopify?.currency?.active || 'EUR';
    return new Intl.NumberFormat(this.currentLanguage, {
      style: 'currency',
      currency: currency
    }).format(cents / 100);
  }

  calculateDiscount(price, comparePrice) {
    if (!comparePrice) return 0;

    const priceNum = parseFloat(price.replace(/[^0-9.,]/g, '').replace(',', '.'));
    const comparePriceNum = parseFloat(comparePrice.replace(/[^0-9.,]/g, '').replace(',', '.'));

    if (comparePriceNum > priceNum) {
      return Math.round((1 - priceNum / comparePriceNum) * 100);
    }

    return 0;
  }

  async addToCart(productId, event, originalId = null) {
    // Use originalId for removal, productId for cart operations
    const removalId = originalId || productId;
    console.log('[Wishlist] AddToCart called - productId:', productId, 'removalId:', removalId);

    try {
      // Get the button that was clicked
      const button = event?.target?.closest('[data-wishlist-add-cart]');

      // Get the cached product data
      let cachedProduct = null;

      // Check all cache keys to find the product
      for (const [key, value] of this.productCache.entries()) {
        if (value.id == productId || value.productId == productId || key.includes(productId)) {
          cachedProduct = value;
          break;
        }
      }

      console.log('[Wishlist] Add to cart - cached product:', cachedProduct);

      // ALWAYS check for variant wizard need if product has multiple variants
      if (cachedProduct && cachedProduct.handle) {
        // Fetch fresh product data to ensure we have all variants
        const response = await fetch(`/products/${cachedProduct.handle}.js`).catch(() => null);

        if (response?.ok) {
          const productData = await response.json();

          // Check if product has multiple variants (not just multiple available)
          const hasMultipleVariants = productData.variants && productData.variants.length > 1;
          const availableVariants = productData.variants?.filter(v => v.available) || [];
          const hasOptions = productData.options &&
                            productData.options.length > 0 &&
                            productData.options.some(opt => opt !== 'Title' && opt !== 'Default Title');

          console.log('[Wishlist] Variant check:', {
            hasMultipleVariants,
            availableVariantsCount: availableVariants.length,
            hasOptions,
            totalVariants: productData.variants?.length
          });

          // Open variant wizard if product has multiple variants (even if only one is available)
          if (hasMultipleVariants && hasOptions && window.BeezfyVariantWizard) {
            console.log('[Wishlist] Opening variant wizard for product selection');

            // Store the original product ID that was used in the wishlist
            // This ensures we remove the correct item
            const wishlistProductId = removalId; // Use the removal ID (original ID from wishlist)
            const self = this;

            // Open variant wizard for product selection
            window.BeezfyVariantWizard.open(productData, {}, async (variant) => {
              // Callback when variant is selected
              if (variant && variant.id) {
                console.log('[Wishlist] Variant selected:', variant.id);

                // Use centralized ATC system
                if (window.BeezfyATCSystem && typeof window.BeezfyATCSystem.addToCart === 'function') {
                  try {
                    await window.BeezfyATCSystem.addToCart(variant.id, 1, {
                      button: button,
                      source: 'wishlist'
                    });

                    // Remove from wishlist after successful add to cart
                    setTimeout(() => {
                      // Use the existing removeProduct method which handles everything properly
                      console.log('[Wishlist] Removing variant after cart addition:', wishlistProductId);
                      self.removeProduct(wishlistProductId);

                      // Show notification that it was removed after adding to cart
                      self.showNotification(self.t('addedToCartAndRemoved') || 'Added to cart and removed from wishlist', 'success');
                    }, 1000);
                  } catch (error) {
                    console.error('[Wishlist] Error adding to cart:', error);
                  }
                }
              }
            });
            return; // Exit here, variant wizard will handle the rest
          }

          // For single variant products, use the first available variant
          if (availableVariants.length === 1) {
            productId = availableVariants[0].id;
          } else if (cachedProduct.id) {
            productId = cachedProduct.id;
          }
        }
      }

      // Ensure we have a valid variant ID for direct ATC
      let variantId = productId;

      // If it's not a large number (variant ID), we need to fetch the product to get variant ID
      if (!/^\d{10,}$/.test(variantId.toString())) {
        // Try to fetch product data to get the first available variant
        const productHandle = cachedProduct?.handle || productId;
        const response = await fetch(`/products/${productHandle}.js`).catch(() => null);

        if (response?.ok) {
          const productData = await response.json();
          // Get first available variant
          if (productData.variants && productData.variants.length > 0) {
            const availableVariant = productData.variants.find(v => v.available) || productData.variants[0];
            variantId = availableVariant.id;
          }
        } else {
          this.showNotification(this.t('error'), 'error');
          return;
        }
      }

      // Validate variant ID before sending
      if (!variantId || !/^\d+$/.test(variantId.toString())) {
        this.showNotification(this.t('unavailable'), 'error');
        return;
      }

      // Use centralized ATC system if available
      if (window.BeezfyATCSystem && typeof window.BeezfyATCSystem.addToCart === 'function') {
        // Get the button that was clicked for animation
        const button = event?.target?.closest('[data-wishlist-add-cart]');


        // Call centralized ATC system with variant ID
        try {
          await window.BeezfyATCSystem.addToCart(variantId, 1, {
            button: button,
            source: 'wishlist'
          });

          // Remove from wishlist after successful add to cart
          const self = this;
          setTimeout(() => {
            // Use the existing removeProduct method which handles everything properly
            console.log('[Wishlist] Removing after cart addition:', removalId);
            self.removeProduct(removalId);

            // Show notification that it was removed after adding to cart
            self.showNotification(self.t('addedToCartAndRemoved') || 'Added to cart and removed from wishlist', 'success');
          }, 1000);
        } catch (error) {
          console.error('[Wishlist] Error adding to cart:', error);
        }

        // Notification and cart drawer are handled by ATC system
      } else {
        // Fallback if ATC system not available
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 })
        });

        if (response.ok) {
          this.showNotification(this.t('addedToCart'), 'success');

          // Update cart if drawer system exists
          if (window.BeezfyDrawerSystem && typeof window.BeezfyDrawerSystem.open === 'function') {
            window.BeezfyDrawerSystem.open('cart');
          } else if (window.drawerSystem && typeof window.drawerSystem.open === 'function') {
            window.drawerSystem.open('cart');
          }

          // Dispatch cart update event
          window.dispatchEvent(new CustomEvent('cart:updated'));
        } else {
          throw new Error('Failed to add to cart');
        }
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      this.showNotification(this.t('error'), 'error');
    }
  }

  // Method removed - addAllToCart violates ATC centralized system rules

  removeFromWishlistAfterCart(productId) {
    console.log('[Wishlist] Removing:', productId);

    // Convert productId to string for consistent comparison
    const targetId = String(productId);

    // Remove from data immediately
    const originalLength = this.wishlistData.length;

    this.wishlistData = this.wishlistData.filter(item => {
      // Check multiple possible ID fields
      if (typeof item === 'object') {
        // Check if any ID field matches
        const itemId = String(item.id || '');
        const itemProductId = String(item.productId || '');
        const itemHandle = String(item.handle || '');

        // Don't keep if any ID matches
        const shouldRemove = itemId === targetId ||
                           itemProductId === targetId ||
                           itemHandle === targetId;

        if (shouldRemove) {
          console.log('[Wishlist] Found match - removing:', item);
        }

        return !shouldRemove;
      } else {
        // Simple string comparison
        return String(item) !== targetId;
      }
    });

    console.log('[Wishlist] Removed:', originalLength - this.wishlistData.length, 'items');

    // Only proceed if we actually removed something
    if (originalLength > this.wishlistData.length) {
      // Save immediately to localStorage
      this.saveWishlist();
      this.syncButtons();

      // Find the item element in the DOM for animation
      const itemElement = document.querySelector(`[data-product-id="${targetId}"]`);

      // Animate removal if element exists
      if (itemElement) {
        itemElement.classList.add('removing');

        setTimeout(() => {
          // Update drawer if open
          if (this.isDrawerOpen()) {
            this.renderWishlistDrawer();
          }
        }, 400);
      } else if (this.isDrawerOpen()) {
        // Update drawer immediately if no element to animate
        this.renderWishlistDrawer();
      }

      // Don't show notification - let the cart notification handle it
    }
  }

  clearWishlist(confirm = true) {
    if (confirm && !window.confirm(this.t('confirmClear'))) {
      return;
    }

    this.wishlistData = [];
    this.saveWishlist();
    this.syncButtons();

    if (this.isDrawerOpen()) {
      this.renderWishlistDrawer();
    }

    this.showNotification(this.t('cleared'), 'info');
  }

  shareWishlist() {
    const wishlistIds = this.wishlistData.map(item =>
      typeof item === 'string' ? item : item.id
    ).join(',');

    const shareUrl = `${window.location.origin}/pages/wishlist?shared=${wishlistIds}`;

    // Copy to clipboard
    if (navigator.clipboard) {
      navigator.clipboard.writeText(shareUrl).then(() => {
        this.showNotification('Link copiato!', 'success');
      });
    } else {
      // Fallback
      const input = document.createElement('input');
      input.value = shareUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      this.showNotification('Link copiato!', 'success');
    }
  }

  openDrawer() {
    // Always render content before opening
    this.renderWishlistDrawer();

    // Check if drawer system exists and has the correct method
    if (window.BeezfyDrawerSystem && typeof window.BeezfyDrawerSystem.open === 'function') {
      window.BeezfyDrawerSystem.open('wishlist');
    } else if (window.drawerSystem && typeof window.drawerSystem.open === 'function') {
      window.drawerSystem.open('wishlist');
    } else {
      // Fallback - apri manualmente
      const drawer = document.querySelector(this.selectors.drawer);
      if (drawer) {
        drawer.classList.add('active');
        drawer.style.display = 'block';
        drawer.style.transform = 'translateX(0)';
      }
    }
  }

  isDrawerOpen() {
    const drawer = document.querySelector(this.selectors.drawer);
    return drawer && drawer.classList.contains('active');
  }

  showNotification(message, type = 'info') {
    // Remove existing notification
    document.querySelectorAll('.beezfy-wishlist-notification').forEach(el => el.remove());

    const notification = document.createElement('div');
    notification.className = `beezfy-wishlist-notification beezfy-wishlist-notification--${type}`;

    const icon = this.getNotificationIcon(type);

    notification.innerHTML = `
      <div class="beezfy-wishlist-notification__icon">${icon}</div>
      <div class="beezfy-wishlist-notification__text">${message}</div>
    `;

    document.body.appendChild(notification);

    // Animate in
    requestAnimationFrame(() => {
      notification.classList.add('show');
    });

    // Auto hide
    setTimeout(() => {
      notification.classList.remove('show');
      setTimeout(() => notification.remove(), 400);
    }, 3000);
  }

  getNotificationIcon(type) {
    const icons = {
      success: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>',
      error: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>',
      info: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>'
    };
    return icons[type] || icons.info;
  }

  t(key) {
    return this.translations[this.currentLanguage]?.[key] ||
           this.translations['en'][key] ||
           key;
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  console.log('[Wishlist] Waiting for DOMContentLoaded...');
  document.addEventListener('DOMContentLoaded', () => {
    console.log('[Wishlist] DOMContentLoaded - Initializing...');
    window.beezfyWishlistPremium = new BeezfyWishlistPremium();
  });
} else {
  console.log('[Wishlist] DOM already loaded - Initializing...');
  window.beezfyWishlistPremium = new BeezfyWishlistPremium();
}

// Export for use in other modules
if (typeof module !== 'undefined' && module.exports) {
  module.exports = BeezfyWishlistPremium;
}